/* Supply › Reports › Sales › Outstanding Payments
 * Invoices with a balance due (amount > paid). */
import React, { useEffect, useMemo, useState } from "react";
import supplyService from "../../../Services/supplyService";
import { cbFmtINR, cbFmtNum, cbStatusVariant } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
} from "../supplyKit";
import { exportToXlsx } from "./reportUtils";
import { ReportBack } from "./reportShared";
import { KIND_LABEL } from "./SalesRegister";
import { PHARMACY_ROUTES } from "../../../Route/path";

const SalesOutstanding = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    supplyService
      .getInvoices()
      .then((rows) => {
        if (!alive) return;
        setInvoices(Array.isArray(rows) ? rows : []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load outstanding payments.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return invoices
      .map((inv) => ({ ...inv, due: (Number(inv.amount) || 0) - (Number(inv.paid) || 0) }))
      .filter((inv) => inv.due > 0)
      .filter((inv) =>
        needle ? [inv.id, inv.customerName].filter(Boolean).join(" ").toLowerCase().includes(needle) : true
      )
      .sort((a, b) => b.due - a.due);
  }, [invoices, q]);

  const totalDue = rows.reduce((s, r) => s + r.due, 0);

  const handleExport = () => {
    const headers = ["Invoice", "Customer", "Channel", "Issued", "Due date", "Amount", "Paid", "Outstanding", "Status"];
    const body = rows.map((inv) => [
      inv.id,
      inv.customerName || "",
      KIND_LABEL[inv.kind] || inv.kind || "",
      inv.issued || "",
      inv.due_date || inv.due_dt || inv.dueDate || inv.due || "",
      Number((Number(inv.amount) || 0).toFixed(2)),
      Number((Number(inv.paid) || 0).toFixed(2)),
      Number(inv.due.toFixed(2)),
      inv.status || "",
    ]);
    exportToXlsx("outstanding-payments.xlsx", headers, body, "Outstanding");
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.SALES_REPORT} label="Sales Reports" />
      <SectionHeader
        title="Outstanding Payments"
        sub="Invoices with a balance still due"
        actions={
          <Button icon={<IconDownload size={14} />} onClick={handleExport} disabled={!rows.length}>
            Export
          </Button>
        }
      />

      <ToolbarBar>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice or customer…" style={{ width: 240, maxWidth: "100%" }} />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(rows.length)} invoices
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Open invoices" value={cbFmtNum(rows.length)} variant="amber" />
        <KpiBox label="Total outstanding" value={cbFmtINR(totalDue)} variant="red" />
      </div>

      {loading ? (
        <LoadingState label="Loading outstanding payments…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "Invoice" },
            { label: "Customer" },
            { label: "Channel" },
            { label: "Issued" },
            { label: "Amount", align: "right" },
            { label: "Paid", align: "right" },
            { label: "Outstanding", align: "right" },
            { label: "Status" },
          ]}
        >
          {rows.length === 0 && (
            <tr>
              <Td colSpan={8} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                No outstanding invoices.
              </Td>
            </tr>
          )}
          {rows.map((inv) => (
            <Tr key={inv.id}>
              <Td mono style={{ fontWeight: 600 }}>
                {inv.id}
              </Td>
              <Td>{inv.customerName}</Td>
              <Td>
                <Pill variant="neutral">{KIND_LABEL[inv.kind] || inv.kind}</Pill>
              </Td>
              <Td mono>{inv.issued}</Td>
              <Td mono right>
                {cbFmtINR(inv.amount)}
              </Td>
              <Td mono right>
                {cbFmtINR(inv.paid)}
              </Td>
              <Td mono right style={{ fontWeight: 600, color: "var(--red-700)" }}>
                {cbFmtINR(inv.due)}
              </Td>
              <Td>
                <Pill variant={cbStatusVariant(inv.status)} dot>
                  {inv.status}
                </Pill>
              </Td>
            </Tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export default SalesOutstanding;
